import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Locale } from '../lib/dates';
import { readStorage, STORAGE_KEYS, writeStorage } from '../lib/storage';
import { en } from './en';
import { mk, type MessageKey } from './mk';

export type { Locale } from '../lib/dates';
export type { MessageKey } from './mk';

export const LOCALES: readonly Locale[] = ['mk', 'en'];

/**
 * The language a build starts in (M5): English unless built with VITE_DEFAULT_LOCALE=mk (the
 * builds for the validation study in Skopje). Anything else is English.
 */
export function buildDefaultLocale(configured: unknown = import.meta.env.VITE_DEFAULT_LOCALE): Locale {
  return typeof configured === 'string' && configured.trim().toLowerCase() === 'mk' ? 'mk' : 'en';
}

export const DEFAULT_LOCALE: Locale = buildDefaultLocale();

/** The language switch shows each language in its own name (docs/i18n/glossary-mk.md). */
export const LANGUAGE_NAMES: Record<Locale, string> = { mk: 'Македонски', en: 'English' };

const DICTIONARIES: Record<Locale, Record<MessageKey, string>> = { mk, en };

export type MessageParams = Record<string, string | number>;

/** The message for `key` in `locale` with {placeholders} filled in (plain text, never HTML). */
export function translate(locale: Locale, key: MessageKey, params?: MessageParams): string {
  const template = DICTIONARIES[locale][key] ?? en[key] ?? key;
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : whole,
  );
}

export function isLocale(value: unknown): value is Locale {
  return value === 'mk' || value === 'en';
}

/**
 * The language the user picked in this browser (localStorage, guarded) — it always wins — else
 * the build's default (English; Macedonian with VITE_DEFAULT_LOCALE=mk).
 */
export function storedLocale(fallback: Locale = DEFAULT_LOCALE): Locale {
  const saved = readStorage('local', STORAGE_KEYS.locale);
  return isLocale(saved) ? saved : fallback;
}

export interface I18n {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, params?: MessageParams) => string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? storedLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = `ZINQ — ${translate(locale, 'app.panelName')}`;
  }, [locale]);

  // The user's explicit choice: remembered even when it is the language already shown, so a
  // build with another default never overrides it.
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    writeStorage('local', STORAGE_KEYS.locale, next);
  }, []);

  const value = useMemo<I18n>(
    () => ({
      locale,
      setLocale,
      t: (key, params) => translate(locale, key, params),
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n outside <I18nProvider>');
  return ctx;
}
